'use client';

import { CABINA_PRIMARIA_PLACEHOLDER, isValidCabinaPrimaria } from '@slims/shared/constants';
import { checkPlantRequirements, type PlantRequirementIssue } from '@slims/shared/validation';
import {
	MemberMeterView,
	type MemberMeterPlantField,
	type MemberMeterPlantIssue,
} from '@slims/ui/organisms';
import { toast } from 'sonner';
import { BollettaUploadSection } from '@/components/members/bolletta-upload-section';
import { MeterReaderSection } from '@/components/members/meter-reader-section';
import { TelemetryPanel } from '@/components/members/telemetry-panel';
import { TypicalProfileSection } from '@/components/members/typical-profile-section';
import { useMeterDraft } from '@/components/members/use-meter-draft';
import { meterDraftFields } from '@/components/members/meter-draft-fields';
import type { Meter } from '@/hooks/use-meters';

interface ProduzioneTabProps {
	meter?: Meter;
	onUpdate: (id: string, patch: Record<string, unknown>) => Promise<unknown>;
	onOpenGateway: (meter: Meter) => void;
	/** Dati del socio da cui il profilo tipico è dedotto. */
	ruolo?: string | null;
	tipo?: string | null;
	hasStorage?: boolean | null;
	/** Registra il salvataggio di questo tab, così il "Salva" della pagina può invocarlo. */
	onRegisterSave?: (save: () => Promise<boolean>) => void;
	/** Il misuratore ha modifiche non salvate. */
	onDirtyChange?: (dirty: boolean) => void;
}

const NUMERIC = new Set([
	'potenzaKwp',
	'pnrrGrantPct',
	'numeroModuli',
	'potenzaModuloW',
	'potenzaInverterKw',
]);

const ISSUE_FIELD: Record<PlantRequirementIssue['campo'], MemberMeterPlantField> = {
	potenzaKwp: 'potenzaKwp',
	dataEsercizio: 'dataEntrataEsercizio',
	pnrrGrantPct: 'pnrrGrantPct',
};

/**
 * Requisiti RO sui valori del form, mentre si scrivono (INB-322). La data si
 * controlla solo quando c'è una potenza: senza, l'impianto non esiste ancora e
 * «data obbligatoria» su un form vuoto sarebbe rumore.
 */
function plantIssuesFor(
	str: (k: 'potenzaKwp' | 'dataEntrataEsercizio' | 'pnrrGrantPct') => string,
): Partial<Record<MemberMeterPlantField, MemberMeterPlantIssue>> {
	const potenza = str('potenzaKwp').trim();
	const grant = str('pnrrGrantPct').trim();
	const issues = checkPlantRequirements({
		potenzaKwp: potenza ? Number(potenza) : undefined,
		dataEsercizio: potenza ? str('dataEntrataEsercizio') : undefined,
		pnrrGrantPct: grant ? Number(grant) : undefined,
	});
	const byField: Partial<Record<MemberMeterPlantField, MemberMeterPlantIssue>> = {};
	for (const issue of issues) byField[ISSUE_FIELD[issue.campo]] ??= issue;
	return byField;
}

/** Gli avvisi che l'API restituisce dopo il salvataggio, fra cui il limite del 30% sulla configurazione. */
function toastPlantWarnings(result: unknown) {
	const avvisi = (result as { avvisiImpianto?: PlantRequirementIssue[] } | null)?.avvisiImpianto;
	for (const avviso of avvisi ?? []) toast.warning(avviso.messaggio, { duration: 10_000 });
}

export function ProduzioneTab({
	meter,
	onUpdate,
	onOpenGateway,
	ruolo,
	tipo,
	hasStorage,
	onRegisterSave,
	onDirtyChange,
}: ProduzioneTabProps) {
	const d = useMeterDraft(
		meter,
		async (id, patch) => {
			const result = await onUpdate(id, patch);
			toastPlantWarnings(result);
			return result;
		},
		NUMERIC,
		onRegisterSave,
		onDirtyChange,
	);
	return (
		<MemberMeterView
			kind="M2"
			fields={meter ? meterDraftFields(d) : undefined}
			onFieldChange={d.set}
			plantIssues={meter ? plantIssuesFor(d.str) : undefined}
			cabinaPlaceholder={CABINA_PRIMARIA_PLACEHOLDER}
			cabinaError={
				isValidCabinaPrimaria(d.str('cabinaPrimaria'))
					? undefined
					: `Formato non valido: usa un codice come ${CABINA_PRIMARIA_PLACEHOLDER}`
			}
			typicalProfile={
				meter && (
					<TypicalProfileSection
						meter={meter}
						ruolo={ruolo}
						tipo={tipo}
						hasStorage={hasStorage}
						onUpdate={onUpdate}
					/>
				)
			}
			bill={meter && <BollettaUploadSection meter={meter} />}
			curve={
				meter && (
					<TelemetryPanel
						meterId={meter.id}
						podId={meter.podId}
						series="immessa"
						seriesLabel="Produzione"
						accent="#00e5ff"
					/>
				)
			}
			reader={
				meter && (
					<MeterReaderSection meter={meter} onUpdate={onUpdate} onOpenGateway={onOpenGateway} />
				)
			}
		/>
	);
}
